<template>
    <div v-if="relations.length > 0" class="mt-12 space-y-10">
        <div class="p-card">
            <ExpandingCreateButton
                v-if="canAddRelations && currentRelation"
                :modelClass="modelClass"
                :text="currentRelation.create_button_text"
                :url="currentRelation.create_route"
                :data="currentRelation.create_route_params"
                :dataOptions="currentRelation.create_route_parameter_options"
                :invertedColors="true"
                class="pb-3 -mr-1"
            />
            <Tabs
                @tabChanged="tabChanged"
                :tabs="relations"
                :enableReactiveHeightTransition="true"
                :avoidBreakingWords="true"
            >
                <template v-for="relation in relations" v-slot:[relation.id]>
                    <div
                        :class="[
                            { 'pb-5': relation.pagination?.data?.length == 0 },
                        ]"
                    >
                        <p
                            v-if="
                                canAddRelations &&
                                relation.pagination?.data?.length == 0
                            "
                            class="text-center text-gray-500 mt-5 font-medium"
                            v-text="$lang.get('Nothing added yet')"
                        />
                        <Transition name="fade" mode="out-in">
                            <div
                                :key="pageNumbers[relation.id]"
                                :class="[
                                    {
                                        'divide-y divide-border':
                                            relation.modelClass != 'resource',
                                    },
                                    'flex flex-col rounded-md gap-y-4',
                                ]"
                            >
                                <div
                                    v-for="(item, index) in relation.pagination
                                        .data"
                                    v-if="relation.pagination?.data?.length"
                                    :class="{
                                        'pt-4': index > 0,
                                        'pt-0': index == 0,
                                    }"
                                >
                                    <ResourceItem
                                        v-if="relation.modelClass == 'resource'"
                                        @deleteModel="confirmDeleteModel"
                                        :item="item"
                                        :generateUrl="true"
                                        :modelClass="relation.modelClass"
                                        displayVariant="maincolumn-relation-panel"
                                        :resourceableModelClass="modelClass"
                                        :availableEmotions="availableEmotions"
                                        :class="`border border-border rounded-card p-card`"
                                    />
                                    <DetailedListItem
                                        v-else
                                        @deleteModel="confirmDeleteModel"
                                        :item="item"
                                        :generateUrl="true"
                                        :modelClass="relation.modelClass"
                                        displayVariant="maincolumn-relation-panel"
                                        :panelModalClass="modelClass"
                                        :showOptionsButton="true"
                                    />
                                </div>
                            </div>
                        </Transition>
                        <Pagination
                            v-if="relation.pagination?.data?.length > 0"
                            @ChangePage="
                                (page) => changePage(relation.id, page)
                            "
                            :currentPage="relation.pagination.current_page"
                            :perPage="relation.pagination.per_page"
                            :rowsCount="relation.pagination.total"
                            :totalCount="relation.pagination.total"
                            :lastPage="relation.pagination.last_page"
                            class="pt-3"
                        />
                    </div>
                </template>
            </Tabs>
        </div>
        <ConfirmDeleteBaseModelModal
            v-if="$isAdmin"
            ref="confirmDeleteModal"
            :modelClass="modelToBeDeleted.model_class || null"
            :modelId="modelToBeDeleted.model_id || null"
            @delete="deleteModel"
            :emitInsteadOfDelete="true"
        />
    </div>
</template>

<script>
import Tabs from '@/Components/Utilities/Tabs.vue'
import ReactiveHeightTransition from '@/Components/Transitions/ReactiveHeightTransition.vue'
import Pagination from '@/Components/DataTable/Pagination.vue'
import ResourceItem from '@/Components/Panels/RelationPanel/ResourceItem.vue'
import DetailedListItem from '@/Components/Panels/DetailedListItem.vue'
import { upperCaseFirst } from '@/Tools/utilities'
import { router } from '@inertiajs/vue3'
import ExpandingCreateButton from '@/Components/ModelComponents/ExpandingCreateButton.vue'
import ConfirmDeleteBaseModelModal from '@/Components/Modals/ConfirmDeleteBaseModelModal.vue'

export default {
    name: 'RelationPanel',

    components: {
        Tabs,
        ReactiveHeightTransition,
        Pagination,
        ResourceItem,
        DetailedListItem,
        ExpandingCreateButton,
        ConfirmDeleteBaseModelModal,
    },

    props: {
        relations: {
            type: Array,
            required: true,
            validator: (relations) => {
                return relations.every((relation) => {
                    return (
                        Object.hasOwn(relation, 'id') &&
                        Object.hasOwn(relation, 'title') &&
                        Object.hasOwn(relation, 'modelClass') &&
                        Object.hasOwn(relation, 'pagination') &&
                        Object.hasOwn(relation, 'create_route') &&
                        Object.hasOwn(relation, 'create_route_params')
                    )
                })
            },
        },

        modelClass: {
            type: String,
            required: true,
        },

        canAddRelations: {
            type: Boolean,
            required: true,
        },

        availableEmotions: {
            type: Array,
            required: true,
        },
    },

    data() {
        return {
            pageNumbers: {},
            currentTabIndex: null,
            modelToBeDeleted: {},
        }
    },

    mounted() {
        this.relations.forEach((relation) => {
            this.pageNumbers[relation.modelClass] =
                relation.pagination?.current_page
        })

        this.currentTabIndex = 0
    },

    computed: {
        currentRelation() {
            return this.relations[this.currentTabIndex]
        },
    },

    methods: {
        upperCaseFirst,

        changePage(relationId, pageNumber) {
            this.pageNumbers[relationId] = pageNumber

            router.reload({
                only: [relationId],
                data: {
                    [`${relationId}_page`]: pageNumber,
                },
                preserveScroll: true,
                preserveState: true,
                replace: true,
            })
        },

        /**
         * When the tab is changed, we reset the pagination of the previous tab
         * if it is not already on the first page
         */
        tabChanged({ from, to }) {
            this.currentTabIndex = to
            if (this.pageNumbers[this.relations[from].id] !== 1) {
                this.changePage(this.relations[from].id, 1)
            }
        },
        confirmDeleteModel(model) {
            this.modelToBeDeleted = {
                model_id: model.id,
                model_class: model.modelClass,
            }
            this.$nextTick(() => {
                this.$refs.confirmDeleteModal.confirmDelete()
            })
        },

        deleteModel({ model_id, model_class }) {
            axios
                .delete(
                    this.$localizedRoute(`api.${model_class}.destroy`, model_id)
                )
                .then((response) => {
                    // Check if the model was the only one on the page
                    let lastModelOnPage =
                        this.currentRelation.pagination.data.length == 1
                    // Check if we are on the first page
                    let isOnFirstPage =
                        this.currentRelation.pagination.current_page == 1

                    // If the model was the only one on the page and we are viewing the first page or if the model was not the only one on the page, we reload the page
                    if (
                        (lastModelOnPage && isOnFirstPage) ||
                        !lastModelOnPage
                    ) {
                        this.changePage(
                            this.currentRelation.id,
                            this.currentRelation.pagination.current_page
                        )
                        // Otherwise we check if the model was the only one on the page and we are not on the first page, if so we fetch the previous page
                    } else if (lastModelOnPage && !isOnFirstPage) {
                        this.changePage(
                            this.currentRelation.id,
                            this.currentRelation.pagination.current_page - 1
                        )
                    }
                })
        },
    },
}
</script>
